/**
 * Playback engine.
 *
 * Runs in the renderer, driven entirely by the plan the main process computes from local data.
 * Two stacked layers cross-fade (or slide) so there is never a blank frame between items, and
 * every item reports proof of play when it finishes.
 *
 * Design rules that matter on a 24/7 screen:
 *  - never leave the screen blank: on any error, advance to the next item instead of stopping;
 *  - never trust a video to fire `ended`: a hard timeout advances a stuck item;
 *  - keep exactly one timer alive so a plan change cannot leave orphaned schedules running.
 */

import { builtInMenuPack, loadMenuPack, type MenuBoardContent, type MenuPack } from '@signage/shared/menu';
import { MENU_FONT_FACES } from '@signage/shared/menu-fonts';

export type ScalingMode = 'fit' | 'fill' | 'stretch' | 'original';
export type Transition = 'none' | 'fade' | 'slide';

export interface MenuPlaybackPage {
  board: MenuBoardContent;
  page: number;
  pageCount: number;
  orientation: 'landscape' | 'portrait';
  images: Record<string, { source: string; sha256: string }>;
  /** The downloaded menu pack the pages were cut with; absent or null for the built-in one. */
  packSha256?: string | null;
}

export interface PlaybackItem {
  key: string;
  mediaId: string;
  kind: 'image' | 'video' | 'menu';
  mimeType: string;
  source: string | null;
  sha256: string;
  durationMs: number;
  useNaturalDuration: boolean;
  transition: Transition;
  transitionMs: number;
  scaling: ScalingMode;
  muted: boolean;
  playlistId: string | null;
  slideshowId: string | null;
  scheduleId: string | null;
  name?: string;
  menu?: MenuPlaybackPage;
}

export interface PlaybackPlan {
  scheduleId: string | null;
  scheduleName: string | null;
  contentType: string | null;
  contentId: string | null;
  playlistId: string | null;
  loop: boolean;
  items: PlaybackItem[];
  nextBoundary: string | null;
  timezone: string;
  unavailableMediaIds: string[];
  /** The downloaded menu designs the menu pages were cut with, as source to run. */
  menuPack?: { sha256: string; source: string } | null;
}

export interface PlaybackCallbacks {
  onItemStart: (item: PlaybackItem) => void;
  onItemComplete: (item: PlaybackItem, startedAt: Date, completedAt: Date, completed: boolean) => void;
  onError: (code: string, message: string, context?: Record<string, unknown>) => void;
  onIdle: () => void;
}

interface Layer {
  root: HTMLDivElement;
  image: HTMLImageElement;
  video: HTMLVideoElement;
  /** Holds a rendered menu page. */
  menu: HTMLDivElement;
}

const MAX_ITEM_MS = 6 * 60 * 60 * 1000;
/** Extra time a full-length video gets beyond its length before the backstop moves on. */
const VIDEO_END_GRACE_MS = 2000;

/**
 * Whether a plan item already carries a usable URL.
 *
 * Anything with a scheme is taken as-is; anything else is a bare reference the host has to
 * resolve against wherever it keeps its cache. This used to test for `media://` specifically,
 * which is the Electron player's custom protocol — shared code had no business knowing that, and
 * on Android it meant every image resolved to a scheme the WebView cannot load.
 */
function isAbsoluteUrl(source: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(source);
}

export class PlaybackEngine {
  private plan: PlaybackPlan | null = null;
  private index = 0;
  private layers: [Layer, Layer];
  private frontLayer = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private watchdogTimer: ReturnType<typeof setTimeout> | null = null;
  private currentItem: PlaybackItem | null = null;
  private currentStartedAt: Date | null = null;
  private generation = 0;
  private consecutiveFailures = 0;
  private mediaUrlFor: (sha256: string) => string;
  /** Menu packs by sha256; null for one that failed to load. */
  private readonly menuPacks = new Map<string, MenuPack | null>();
  /** The pack whose CSS is in the document (null: the built-in designs' CSS only). */
  private menuPackCss: string | null = null;

  constructor(
    private readonly container: HTMLElement,
    private readonly callbacks: PlaybackCallbacks,
    mediaUrlFor: (sha256: string) => string,
  ) {
    this.mediaUrlFor = mediaUrlFor;
    this.layers = [this.createLayer(), this.createLayer()];
    for (const layer of this.layers) this.container.appendChild(layer.root);
    if (!document.getElementById('menu-board-css')) {
      const style = document.createElement('style');
      style.id = 'menu-board-css';
      // The fonts are embedded, so menus look the same with no network at all.
      style.textContent = MENU_FONT_FACES + builtInMenuPack().MENU_CSS;
      document.head.appendChild(style);
    }
  }

  private createLayer(): Layer {
    const root = document.createElement('div');
    root.className = 'layer';

    const image = document.createElement('img');
    image.className = 'media';
    image.decoding = 'async';
    image.draggable = false;

    const video = document.createElement('video');
    video.className = 'media';
    video.playsInline = true;
    video.autoplay = false;
    video.controls = false;
    video.preload = 'auto';
    video.muted = true;

    const menu = document.createElement('div');
    menu.className = 'menu-surface';

    root.append(image, video, menu);
    return { root, image, video, menu };
  }

  /**
   * Applies a new plan. When the plan is materially the same, playback continues untouched so
   * a heartbeat or a re-render never restarts the current item.
   */
  setPlan(plan: PlaybackPlan): void {
    if (plan.menuPack) this.runMenuPack(plan.menuPack.sha256, plan.menuPack.source);
    const sameContent =
      this.plan &&
      this.plan.scheduleId === plan.scheduleId &&
      this.plan.items.length === plan.items.length &&
      this.plan.items.every((item, i) => item.key === plan.items[i]?.key);

    this.plan = plan;
    if (sameContent) return;

    this.generation += 1;
    this.index = 0;
    this.consecutiveFailures = 0;
    this.clearTimers();
    if (plan.items.length === 0) {
      this.showNothing();
      this.callbacks.onIdle();
      return;
    }
    void this.playCurrent();
  }

  private clearTimers(): void {
    if (this.timer) clearTimeout(this.timer);
    if (this.watchdogTimer) clearTimeout(this.watchdogTimer);
    this.timer = null;
    this.watchdogTimer = null;
  }

  private showNothing(): void {
    this.finishCurrent(false);
    for (const layer of this.layers) {
      layer.root.classList.remove('visible');
      layer.video.pause();
      layer.video.removeAttribute('src');
      layer.image.removeAttribute('src');
      layer.menu.replaceChildren();
    }
  }

  private finishCurrent(completed: boolean): void {
    if (this.currentItem && this.currentStartedAt) {
      this.callbacks.onItemComplete(
        this.currentItem,
        this.currentStartedAt,
        new Date(),
        completed,
      );
    }
    this.currentItem = null;
    this.currentStartedAt = null;
  }

  private async playCurrent(): Promise<void> {
    const plan = this.plan;
    if (!plan || plan.items.length === 0) return;
    const generation = this.generation;

    if (this.index >= plan.items.length) {
      if (!plan.loop) {
        // A non-looping playlist holds its last frame rather than going black.
        return;
      }
      this.index = 0;
    }

    const item = plan.items[this.index];
    if (!item || !item.source) {
      this.advanceAfterFailure();
      return;
    }

    const backIndex = 1 - this.frontLayer;
    const back = this.layers[backIndex]!;
    const front = this.layers[this.frontLayer]!;
    const url = isAbsoluteUrl(item.source) ? item.source : this.mediaUrlFor(item.sha256);

    applyScaling(back.image, item.scaling);
    applyScaling(back.video, item.scaling);

    try {
      if (item.kind === 'menu') {
        back.video.pause();
        back.video.removeAttribute('src');
        back.video.style.display = 'none';
        back.image.removeAttribute('src');
        back.image.style.display = 'none';
        back.menu.style.display = 'block';
        await this.renderMenu(back.menu, item);
      } else if (item.kind === 'image') {
        back.menu.style.display = 'none';
        back.menu.replaceChildren();
        back.video.pause();
        back.video.removeAttribute('src');
        back.video.style.display = 'none';
        back.image.style.display = 'block';
        await loadImage(back.image, url);
      } else {
        back.menu.style.display = 'none';
        back.menu.replaceChildren();
        back.image.removeAttribute('src');
        back.image.style.display = 'none';
        back.video.style.display = 'block';
        back.video.muted = item.muted;
        back.video.loop = false;
        // LG TVs have one hardware video pipeline: the outgoing video must give it up first.
        if (isWebOs()) {
          releaseVideo(front.video);
          stopAwakeVideo();
        }
        const failedRoutes = await loadVideoWithFallback(back.video, url);
        if (failedRoutes.length > 0) {
          this.callbacks.onError('playback.video_route_failed', failedRoutes.join(' | '), { mediaId: item.mediaId });
        }
      }
    } catch (err) {
      this.callbacks.onError('playback.load_failed', (err as Error).message, {
        mediaId: item.mediaId,
        kind: item.kind,
      });
      this.advanceAfterFailure();
      return;
    }

    if (generation !== this.generation) return;

    // Cross-fade: reveal the prepared layer, then hide the old one.
    const transitionMs = item.transition === 'none' ? 0 : item.transitionMs;
    back.root.style.transitionDuration = `${transitionMs}ms`;
    front.root.style.transitionDuration = `${transitionMs}ms`;
    back.root.classList.remove('slide-in', 'slide-out');
    if (item.transition === 'slide') back.root.classList.add('slide-in');

    this.finishCurrent(true);
    back.root.classList.add('visible');
    front.root.classList.remove('visible');
    this.frontLayer = backIndex;
    if (item.kind !== 'video' && isWebOs()) {
      releaseVideo(front.video);
      startAwakeVideo();
    }

    this.currentItem = item;
    this.currentStartedAt = new Date();
    this.consecutiveFailures = 0;
    this.callbacks.onItemStart(item);

    if (item.kind === 'video') {
      try {
        await back.video.play();
      } catch (err) {
        // Autoplay can be refused; an unplayable video must not stall the loop.
        this.callbacks.onError('playback.video_play_failed', (err as Error).message, {
          mediaId: item.mediaId,
        });
        this.advanceAfterFailure();
        return;
      }
    }

    const durationMs = this.resolveDuration(item, back.video);
    // A video played to its end moves on when it ends. Its length is only a backstop, with room
    // for the time a TV takes to start and the pauses while it buffers: timed from the play()
    // call, the bare length cut the last part off on slower devices.
    const playsToEnd = item.kind === 'video' && item.useNaturalDuration;
    this.timer = setTimeout(() => {
      if (generation === this.generation) this.advance();
    }, playsToEnd ? durationMs + VIDEO_END_GRACE_MS : durationMs);

    if (item.kind === 'video') {
      back.video.onended = () => {
        if (generation === this.generation && item.useNaturalDuration) this.advance();
      };
      back.video.onerror = () => {
        // The browser's own reason, so a failure can be diagnosed from the dashboard.
        const failure = back.video.error;
        this.callbacks.onError('playback.video_error', 'The video failed during playback', {
          mediaId: item.mediaId,
          errorCode: failure?.code ?? null,
          errorMessage: failure?.message || null,
          atSeconds: Number.isFinite(back.video.currentTime) ? Math.round(back.video.currentTime * 10) / 10 : null,
          durationSeconds: Number.isFinite(back.video.duration) ? Math.round(back.video.duration * 10) / 10 : null,
        });
        if (generation === this.generation) this.advance();
      };
      // Belt and braces: if a video neither ends nor errors, move on anyway.
      const hardLimit = Math.min(durationMs + 15_000, MAX_ITEM_MS);
      this.watchdogTimer = setTimeout(() => {
        if (generation === this.generation) {
          this.callbacks.onError('playback.video_stalled', 'A video stalled and was skipped', {
            mediaId: item.mediaId,
          });
          this.advance();
        }
      }, hardLimit);
    }

    // Preload the next item into the now-hidden layer so the swap is instant.
    void this.preloadNext();
  }

  /**
   * Runs a downloaded menu pack once. The runtime verified it against the manifest and loaded it
   * itself before cutting the plan, so this rarely fails; if it does, its pages are drawn with
   * the built-in designs instead.
   */
  private runMenuPack(sha256: string, source: string): void {
    if (this.menuPacks.has(sha256)) return;
    try {
      const pack = loadMenuPack(source);
      this.menuPacks.set(sha256, pack);
      this.useMenuPackCss(sha256, pack);
    } catch (err) {
      this.menuPacks.set(sha256, null);
      this.callbacks.onError('player.menu_pack_failed', `Menu designs could not be loaded: ${(err as Error).message}`, {
        sha256,
      });
    }
  }

  /**
   * Puts a pack's stylesheet in the document. It follows the built-in one, so where both style
   * a design the pack (the newer) wins; only the latest pack's CSS is kept.
   */
  private useMenuPackCss(sha256: string, pack: MenuPack): void {
    if (this.menuPackCss === sha256) return;
    let style = document.getElementById('menu-pack-css');
    if (!style) {
      style = document.createElement('style');
      style.id = 'menu-pack-css';
      document.head.appendChild(style);
    }
    style.textContent = pack.MENU_CSS;
    this.menuPackCss = sha256;
  }

  /**
   * Draws a menu page into a hidden layer and waits for its photos, so the cross-fade reveals a
   * finished board instead of one whose pictures pop in. A photo that fails is simply left out.
   */
  private async renderMenu(target: HTMLDivElement, item: PlaybackItem): Promise<void> {
    const menu = item.menu;
    if (!menu) throw new Error('A menu item arrived without its board');
    const pack = (menu.packSha256 && this.menuPacks.get(menu.packSha256)) || builtInMenuPack();
    target.innerHTML = pack.renderMenuPage({
      board: menu.board,
      page: menu.page,
      orientation: menu.orientation,
      width: this.container.clientWidth || window.innerWidth,
      height: this.container.clientHeight || window.innerHeight,
      // Always through the host's media URL: `source` can be a bare filesystem path (a Windows
      // one looks like a URL scheme), which the renderer rightly refuses to put in an `src`.
      imageUrl: (mediaId) => {
        const image = menu.images[mediaId];
        return image ? this.mediaUrlFor(image.sha256) : null;
      },
    });
    const pictures = [...target.querySelectorAll('img')];
    await Promise.race([
      Promise.all([
        document.fonts.ready,
        ...pictures.map((img) =>
          img.decode().catch(() => {
            img.remove();
          }),
        ),
      ]),
      new Promise((resolve) => setTimeout(resolve, 10_000)),
    ]);
  }

  private resolveDuration(item: PlaybackItem, video: HTMLVideoElement): number {
    if (item.kind === 'video' && item.useNaturalDuration) {
      const natural = Number.isFinite(video.duration) && video.duration > 0
        ? video.duration * 1000
        : item.durationMs;
      return Math.min(Math.max(natural, 1000), MAX_ITEM_MS);
    }
    return Math.min(Math.max(item.durationMs, 250), MAX_ITEM_MS);
  }

  private async preloadNext(): Promise<void> {
    const plan = this.plan;
    if (!plan || plan.items.length < 2) return;
    const nextIndex = (this.index + 1) % plan.items.length;
    const next = plan.items[nextIndex];
    if (!next?.source || next.kind !== 'image') return;
    const url = isAbsoluteUrl(next.source) ? next.source : this.mediaUrlFor(next.sha256);
    const preloader = new Image();
    preloader.src = url;
  }

  private advance(): void {
    this.clearTimers();
    this.index += 1;
    void this.playCurrent();
  }

  /**
   * Advances after an item failed to load or play.
   *
   * Skipping straight to the next item is right for a single bad file, but if *every* item
   * fails (a wiped cache, a permissions problem) an immediate retry would spin the CPU at
   * full speed. Once a whole pass has failed we pause before trying again and let the host
   * know the screen is effectively idle.
   */
  private advanceAfterFailure(): void {
    this.clearTimers();
    this.consecutiveFailures += 1;
    const total = this.plan?.items.length ?? 0;
    this.index += 1;

    if (total > 0 && this.consecutiveFailures >= total) {
      this.consecutiveFailures = 0;
      this.callbacks.onIdle();
      const generation = this.generation;
      this.timer = setTimeout(() => {
        if (generation === this.generation) void this.playCurrent();
      }, 5000);
      return;
    }
    void this.playCurrent();
  }

  /** Used by the identify command: briefly overlay a marker. */
  flashIdentify(durationMs: number): void {
    const overlay = document.createElement('div');
    overlay.className = 'identify-overlay';
    overlay.textContent = 'This screen';
    this.container.appendChild(overlay);
    setTimeout(() => overlay.remove(), durationMs);
  }

  destroy(): void {
    this.generation += 1;
    this.clearTimers();
    this.showNothing();
  }
}

function applyScaling(element: HTMLElement, mode: ScalingMode): void {
  element.classList.remove('scale-fit', 'scale-fill', 'scale-stretch', 'scale-original');
  element.classList.add(`scale-${mode}`);
}

function loadImage(element: HTMLImageElement, url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('Image load timed out')), 30_000);
    element.onload = () => {
      clearTimeout(timeout);
      resolve();
    };
    element.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('The image could not be decoded'));
    };
    element.src = url;
  });
}

function isWebOs(): boolean {
  return /Web0S|webOS/.test(navigator.userAgent);
}

function releaseVideo(element: HTMLVideoElement): void {
  // Clearing a file can raise an error event: it must not read as this video failing.
  element.onerror = null;
  element.onended = null;
  element.pause();
  element.removeAttribute('src');
  element.load();
}

/**
 * LG TVs start their screensaver over still pictures, and only a playing video holds it off. A
 * tiny silent video runs behind images and menus, and gives the pipeline up for real videos.
 */
let awakeVideo: HTMLVideoElement | null = null;

function startAwakeVideo(): void {
  if (!awakeVideo) {
    awakeVideo = document.createElement('video');
    awakeVideo.muted = true;
    awakeVideo.loop = true;
    awakeVideo.style.cssText = 'position:fixed;left:0;bottom:0;width:2px;height:2px;opacity:.01';
    document.body.append(awakeVideo);
  }
  if (!awakeVideo.getAttribute('src')) awakeVideo.src = './keep-awake.mp4';
  void awakeVideo.play().catch(() => {});
}

function stopAwakeVideo(): void {
  if (awakeVideo) releaseVideo(awakeVideo);
}

/** A stored file opens in moments, so a route that hangs must not hold the screen for a minute. */
const STORED_VIDEO_TIMEOUT_MS = 15_000;

/**
 * TVs can hand video to a hardware pipeline that may not read the web player's stored copy. Each
 * route is tried in turn: the stored copy, the server, then a blob URL. Returns the routes that
 * failed before one worked, so a screen's video route can be read from its events.
 */
const blobUrls = new WeakMap<HTMLVideoElement, string>();

async function loadVideoWithFallback(element: HTMLVideoElement, url: string): Promise<string[]> {
  const failures: string[] = [];
  const attempt = async (name: string, source: string, timeoutMs?: number): Promise<boolean> => {
    try {
      await loadVideo(element, source, timeoutMs);
      return true;
    } catch (err) {
      failures.push(`${name}: ${(err as Error).message}`);
      return false;
    }
  };

  if (await attempt('stored', url, STORED_VIDEO_TIMEOUT_MS)) return failures;

  const sha256 = url.split('/').pop() ?? '';
  const signage = (window as unknown as { signage?: { remoteMediaUrl?: (sha256: string) => Promise<string | null> } }).signage;
  const remote = await signage?.remoteMediaUrl?.(sha256).catch(() => null);
  if (remote && (await attempt('server', remote))) return failures;
  if (!remote) failures.push('server: no address');

  const response = await fetch(url).catch(() => null);
  if (response?.ok) {
    const blobUrl = URL.createObjectURL(await response.blob());
    const previous = blobUrls.get(element);
    if (await attempt('blob', blobUrl)) {
      blobUrls.set(element, blobUrl);
      if (previous) URL.revokeObjectURL(previous);
      return failures;
    }
    URL.revokeObjectURL(blobUrl);
  } else {
    failures.push(`blob: read failed (${response?.status ?? 'no response'})`);
  }
  throw new Error(failures.join(' | '));
}

function loadVideo(element: HTMLVideoElement, url: string, timeoutMs = 60_000): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('Video load timed out')), timeoutMs);
    const done = () => {
      clearTimeout(timeout);
      element.oncanplay = null;
      element.onloadeddata = null;
      element.onerror = null;
      element.currentTime = 0;
      resolve();
    };
    // Start from a clean element: one that played this same file a loop ago otherwise resumes
    // its old media pipeline instead of reading the file afresh.
    element.onended = null;
    element.onerror = null;
    element.pause();
    element.removeAttribute('src');
    element.load();
    element.oncanplay = done;
    element.onloadeddata = done;
    element.onerror = () => {
      // Clearing the old file can raise an error event of its own on TVs: it says nothing about this one.
      if (!element.error) return;
      clearTimeout(timeout);
      reject(new Error(`code ${element.error.code} ${element.error.message || 'no detail'}`));
    };
    element.src = url;
    element.load();
  });
}
